import { useState, useRef, useEffect } from 'react';
import { X, Plus, Tag } from 'lucide-react';
import { useContactTags } from '../hooks/useContacts';
import { useCreateTag } from '../hooks/useCategoryMutations';
import type { ContactTagRow } from '../types';

const FALLBACK_COLOR = 'bg-slate-500/15 text-slate-300 border-slate-500/25';

interface TagInputProps {
  /** Array de slugs das tags selecionadas */
  value: string[];
  onChange: (slugs: string[]) => void;
}

export function TagInput({ value, onChange }: TagInputProps) {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: allTags = [], isLoading } = useContactTags();
  const createTag = useCreateTag();

  /* Tags selecionadas com metadados */
  const selectedTags: ContactTagRow[] = value.map(
    (slug) =>
      allTags.find((t) => t.slug === slug) ?? {
        id: slug,
        tenant_id: '',
        label: slug,
        slug,
        color_class: FALLBACK_COLOR,
        active: true,
        sort_order: 0,
      },
  );

  /* Sugestões: disponíveis, não selecionadas, filtrando pelo input */
  const suggestions = allTags.filter(
    (t) =>
      !value.includes(t.slug) &&
      (input === '' || t.label.toLowerCase().includes(input.toLowerCase())),
  );

  const showCreateOption =
    input.trim() !== '' &&
    !allTags.find((t) => t.label.toLowerCase() === input.trim().toLowerCase());

  const hasDropdown = open && (suggestions.length > 0 || showCreateOption);

  /* ─── Ações ─── */
  const addBySlug = (slug: string) => {
    if (!value.includes(slug)) onChange([...value, slug]);
    setInput('');
    setOpen(false);
    inputRef.current?.focus();
  };

  const handleCreate = async () => {
    const label = input.trim();
    if (!label || createTag.isPending) return;
    try {
      const newTag = await createTag.mutateAsync({
        label,
        colorIndex: allTags.length,
      });
      onChange([...value, newTag.slug]);
    } catch {
      // error already toasted by mutation
    }
    setInput('');
    setOpen(false);
    inputRef.current?.focus();
  };

  const removeTag = (slug: string) => {
    onChange(value.filter((s) => s !== slug));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length > 0 && !showCreateOption) {
        addBySlug(suggestions[0].slug);
      } else if (input.trim()) {
        void handleCreate();
      }
    } else if (e.key === 'Backspace' && !input && value.length > 0) {
      removeTag(value[value.length - 1]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  /* Fechar ao clicar fora */
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      {/* Campo com chips + input inline */}
      <div
        className="min-h-[42px] w-full rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2 flex flex-wrap gap-1.5 items-center cursor-text focus-within:border-accent/50 focus-within:bg-midnight-2 transition-all"
        onClick={() => inputRef.current?.focus()}
      >
        {selectedTags.map((tag) => (
          <span
            key={tag.slug}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tag.color_class}`}
          >
            {tag.label}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeTag(tag.slug);
              }}
              className="hover:opacity-70 cursor-pointer leading-none"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}

        <input
          ref={inputRef}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? 'Adicionar tags…' : ''}
          disabled={isLoading}
          className="flex-1 min-w-[110px] bg-transparent text-sm text-ink placeholder-ink-faint outline-none"
        />

        {value.length === 0 && !input && (
          <Tag className="h-3.5 w-3.5 text-ink-subtle pointer-events-none" />
        )}
      </div>

      {/* Dropdown de sugestões */}
      {hasDropdown && (
        <div className="absolute top-full left-0 mt-1 w-full z-50 rounded-xl glass-strong border border-hairline shadow-deep overflow-hidden animate-fade-in">
          {suggestions.slice(0, 8).map((tag) => (
            <button
              key={tag.id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                addBySlug(tag.slug);
              }}
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm hover:bg-white/5 cursor-pointer text-left transition-colors"
            >
              <span
                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tag.color_class}`}
              >
                {tag.label}
              </span>
            </button>
          ))}

          {showCreateOption && (
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                void handleCreate();
              }}
              disabled={createTag.isPending}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-ink-muted hover:bg-white/5 cursor-pointer transition-colors border-t border-hairline disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5 text-accent" />
              <span>
                Criar tag{' '}
                <span className="font-semibold text-ink">"{input}"</span>
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
